import React, { FC, useState } from 'react';
import { useModal } from 'store/modal';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import Spinner from 'components/Spinner';
import { archiveDoc, sendToEmployee, sendToEmployer } from 'routes/client/documents/helpers';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import useInvalidateQueries from 'utils/helpers/useInvalidateQueries';

interface IProps {
  children?: any;
  id: string;
  docHolderId: string;
  buttonText: string;
  handlePreview: (docId: string) => void;
  invalidate: () => void;
  size?: 'sm' | 'lg';
  ariaLabel?: string;
}

const ApproveDoc: FC<IProps> = ({
  id,
  docHolderId,
  buttonText,
  handlePreview,
  invalidate,
  size = 'sm',
  ariaLabel,
}: IProps) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const invalidateQueries = useInvalidateQueries();

  // Approve document
  const handleApprove = (docId: string) =>
    API.post(`documents/${docId}/approve`)
      .then(res => {
        const { message } = res.data;
        showModal({
          type: 'success',
          content: t('documents.table.approvedDocSuccessMsg'),
          onSuccess: () => {
            if (message?.type === 'approve_sign')
              showModal({
                type: 'success',
                heading: t('documents.table.notificationSent'),
                content: message.body,
                successButton: (
                  <Button
                    btnType="primary"
                    onClick={() => {
                      hideModal();
                    }}
                  >
                    {t('common.ok')}
                  </Button>
                ),
              });

            if (message?.type === 'send_to_employee')
              sendToEmployee(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);

            if (message?.type === 'send_to_employer')
              sendToEmployer(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);

            if (message?.type === 'archive')
              archiveDoc(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);
          },
        });
        invalidateQueries([], invalidate);
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );

  // Confirm approval
  const confirmApprove = (docId: string) => {
    showModal({
      type: 'confirm',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('documents.table.confirmApproveDocMsg')}</p>
          <Button
            ariaLabel="Preview document"
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => handlePreviewDoc({ showModal, id: docId, setIsLoading })}
          >
            {!isLoading && t('documents.table.previewDoc')}

            {isLoading && <Spinner size={14} />}
          </Button>
          <hr />
        </>
      ),
      onConfirm: () => handleApprove(docId),
      confirmLabel: t('documents.table.confirmApproveDocLabel'),
    });
  };

  return (
    <div className="d-inline-block ml-2 mb-2 mb-md-0">
      <TooltipWrapper
        id="tooltip-approve"
        overlay={buttonText}
      >
        <Button
          ariaLabel="Approve document"
          type="button"
          btnType="primary"
          size={size}
          onClick={() => confirmApprove(id)}
        >
          <FaCheck size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default ApproveDoc;
